import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '../assets/close.svg';
import { useTranslation } from 'react-i18next';
import { GoEyeClosed, GoEye } from 'react-icons/go';
import { IoMdArrowDropdown } from 'react-icons/io';
import Ca from '../assets/ca.svg';
import Us from '../assets/us.svg';
import Mx from '../assets/mx.svg';
import log from '../utils/logger';
import { useSignupInvite } from '../contexts/buyer-invite';
import api from '../api/api';
import useToast from '../hooks/useToast';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import Toast from './Toast';


const SignupModal = ({ onClose, email, onAlreadyAccount }) => {
  const { t, i18n } = useTranslation();
  useBodyScrollLock(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ flag: Ca, code: '+1', name: 'Canada' });
  const [firstName, setFirstname] = useState('');
  const [lastName, setLastname] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const dropdownRef = useRef(null);
  const { prefillData } = useSignupInvite();
  const { toastMessage, toastType, showToast } = useToast();
  const currentLanguage = i18n.language;


  useEffect(() => {
    if (prefillData) {
      setFirstname(prefillData.firstName);
      setLastname(prefillData.lastName);
      setUserEmail(prefillData.email);
      setSelectedCountry(prefillData.phoneNumber?.country || { flag: Ca, code: '+1', name: 'Canada' });
      setPhoneNumber(prefillData.phoneNumber?.number);
    }
  }, [prefillData]);
  
  const handlePasswordToggle = () => {
    setShowPassword(prevState => !prevState);
  };

  const validateFirstName = (name) => {
    const errors = [];

    if (!name) {
      errors.push(t('firstnameerror1', 'First name is required'));
    } else {
      if (!/^[\p{L}]/u.test(name)) {
        errors.push(t('firstnameerror2', 'Must start with a letter'));
      }

      if (!/^[\p{L}0-9 \-]+$/u.test(name)) {
        errors.push(t('firstnameerror3', 'Only letters, numbers, hyphens, and spaces are allowed'));
      }
    }

    if (errors.length > 0) {
      setFirstNameError(errors.join(', '));
      return false;
    } else {
      setFirstNameError('');
      return true;
    }
  };

  const validateLastName = (name) => {
    const errors = [];

    if (!name) {
      errors.push(t('lastnameerror1', 'Last name is required'));
    } else {
      if (!/^[\p{L}]/u.test(name)) {
        errors.push(t('lastnameerror2', 'Must start with a letter'));
      }

      if (!/^[\p{L}0-9 \-]+$/u.test(name)) {
        errors.push(t('lastnameerror3', 'Only letters, numbers, hyphens, and spaces are allowed'));
      }
    }

    if (errors.length > 0) {
      setLastNameError(errors.join(', '));
      return false;
    } else {
      setLastNameError('');
      return true;
    }
  };

  const validateEmail = (email) => {
    const errors = [];

    if (!email || email.trim() === '') {
      errors.push(t('emailerror1', 'Email is required.'));
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        errors.push(t('emailerror2', 'Please enter a valid email address.'));
      }
    }

    if (errors.length > 0) {
      setEmailError(errors.join(', '));
      return false;
    } else {
      setEmailError('');
      return true;
    }
  };

  const validatePhoneNumber = (number) => {
    const errors = [];

    if (!number || number.trim() === '') {
      setPhoneNumberError('');
      return true;
    }

    const phonePattern = /^[0-9]{6,12}$/;
    if (!phonePattern.test(number)) {
      errors.push(t('phonenumbererror1'));
    }

    if (errors.length > 0) {
      setPhoneNumberError(errors.join(', '));
      return false;
    } else {
      setPhoneNumberError('');
      return true;
    }
  };

  const validatePassword = (pwd) => {
    const errors = [];

    if (pwd.length < 8) {errors.push(t('passwordrequirement1', 'at least 8 characters'));}
    if (pwd.length > 30) {errors.push(t('passwordrequirement2', 'at most 30 characters'));}

    if (!/[A-Z]/.test(pwd)) {errors.push(t('passwordrequirement3', 'an uppercase letter'));}
    if (!/[a-z]/.test(pwd)) {errors.push(t('passwordrequirement4', 'a lowercase letter'));}
    if (!/[0-9]/.test(pwd)) {errors.push(t('passwordrequirement5', 'a number'));}

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) 
    {errors.push(t('passwordrequirement6', 'a special character'));}

    if (errors.length > 0) {
      setPasswordError(t('passwordRequirements', { rules: errors.join(', ') }));
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  const validateCompanyName = (name) => {
    const errors = [];

    if (!name || name.trim() === '') {
      errors.push(t('companyerror1', 'Company name is required.'));
    } else {
      if (name.length < 2) {errors.push(t('companyerror2', 'Company name must be at least 2 characters long.'));}
      if (name.length > 100) {errors.push(t('companyerror3', 'Company name must be at most 100 characters long.'));}
      if (!/^[\p{L}0-9\s&$.,@#'!\?-]+$/u.test(name)) {
        errors.push(t(
          'companyerror4',
          'Company name can only contain letters (including accents), numbers, spaces, and basic punctuation and symbols (e.g.: & , . @ # $ - \' ! ?).',
        ));
      }
    }

    if (errors.length > 0) {
      setCompanyError(errors.join(', '));
      return false;
    } else {
      setCompanyError('');
      return true;
    }
  };

  // Open Country Codes Dropdown & select country 

  const handleOutsideClick = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleDropdownToggle = () => {
    setDropdownOpen(prev => !prev);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
  };

  useEffect(() => {
    if (email) {setUserEmail(email);}
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFirstName(firstName)) {return;}
    if (!validateLastName(lastName)) {return;}
    if (!validateEmail(userEmail)) {return;}
    if (!validatePhoneNumber(phoneNumber)) {return;}
    if (!validatePassword(password)) {return;}
    if (!validateCompanyName(companyName)) {return;}

    const payload = {
      firstName,
      lastName,
      email: userEmail,
      phoneNumber: {
        countryCode: selectedCountry.code,
        number: phoneNumber,
      },
      password,
      companyName,
      language: currentLanguage,
    };

    try {
      const response = await api.post('/user/buyer-register', payload);
      log.log('✅ Success:', response.data);
      onClose();
    } catch (error) {
      if (error.response) {
        log.error('Server Error:', error.response.data);
        showToast(t('serverErrorMessage'), 'error');
      } else if (error.request) {
        log.error('Network Error:', error.request);
        showToast(t('networkErrorMessage'), 'error');
      } else {
        log.error('Error:', error.message);
        showToast(t('serverErrorMessage'), 'error');
      }
    }
  };

  return createPortal (
    <>
      <div className=' z-[61]'>
        <div
          className='fixed bottom-0 lg:top-0 lg:right-0 w-full lg:max-w-[550px] h-[90dvh] lg:h-full overflow-y-auto rounded-t-2xl lg:rounded-none bg-white z-[10000] transition-transform duration-300 transform p-4'
        >
          {/* Close Icon */}
          <div className='flex justify-end w-full pb-2'>
            <button
              className='w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-300 hover:bg-[#e7e6e6bf]'
              onClick={onClose}
            >
              <img src={CloseIcon} alt="CloseIcon" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <h1 className='text-4xl font-semibold mb-3 capitalize'>
              {t('signup')}
            </h1>

            <p className='text-[#918d8d] text-sm'>
              {t('signuptext')}
            </p>
            <p className='text-black text-sm'>
              {email}
            </p>

            <div className='mt-5'>
              <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>{t('First name')} <span className='text-[#ef4444]'>*</span></label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstname(e.target.value)}
                className='rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'
              />
              {firstNameError && <p className='text-red-500 text-sm mt-1'>{firstNameError}</p>}
            </div>

            <div className='mt-5'>
              <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>{t('Last name')} <span className='text-[#ef4444]'>*</span></label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastname(e.target.value)}
                className='rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'
              />
              {lastNameError && <p className='text-red-500 text-sm mt-1'>{lastNameError}</p>}
            </div>

            <div className='mt-5'>
              <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>{t('email')} <span className='text-[#ef4444]'>*</span></label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className='rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'
              />
              {emailError && <p className='text-red-500 text-sm mt-1'>{emailError}</p>}
            </div>

            {/* Phone Number  */}
            <div className='mt-5'>
              <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>
                {t('phone')} <span className='text-[#ef4444]'>*</span>
              </label>
              <div className='flex items-center relative justify-between gap-2 rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'>
                <div
                  className='flex items-center gap-1 cursor-pointer'
                  onClick={handleDropdownToggle}
                >
                  <img src={selectedCountry.flag} alt="flag" className='w-[23px] h-[23px]' />
                  <IoMdArrowDropdown />
                </div>

                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={phoneNumber}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    setPhoneNumber(onlyNums);
                  }}
                  className='placeholder:font-light w-full outline-none h-full flex-1 bg-transparent border-none'
                />

                {/* Countries Dropdown  */}
                {dropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className='bg-white border border-[#ccc] p-[9px] w-full max-w-[calc(100vw-2rem)] lg:max-w-[390px] rounded-[.3rem] absolute top-[55px] left-0 z-50'
                  >
                    <input
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      className='placeholder:font-light w-full outline-none h-[2.5rem] flex-1 bg-transparent border-2 border-black border-opacity-30 rounded-[.4rem] px-[.4rem]'
                    />

                    <ul className='min-h-[8.5rem] h-[8.5rem] overflow-y-scroll boxscrollbar my-2'>
                      {[{
                        flag: Ca,
                        code: '+1',
                        name: 'Canada',
                      }, {
                        flag: Us,
                        code: '+1',
                        name: 'United States',
                      }, {
                        flag: Mx,
                        code: '+52',
                        name: 'Mexico',
                      }].map(country => (
                        <li
                          key={country.name}
                          className='py-1 px-[.4rem] flex items-center cursor-pointer text-base'
                          onClick={() => handleCountrySelect(country)}
                        >
                          <img src={country.flag} alt="flag" className='w-[23px] h-[23px] mr-2' />
                          <p className='mr-1 font-semibold'>{country.name}</p>
                          <p>{country.code}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {phoneNumberError && <p className='text-red-500 text-sm mt-1'>{phoneNumberError}</p>}
            </div>


            <div className='mt-5'>
              <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>
                {t('password')} <span className='text-[#ef4444]'>*</span>
              </label>
              <div className='flex items-center justify-between gap-2 rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='placeholder:font-light w-full outline-none h-full flex-1 bg-transparent border-none'
                />

                {/* Show/Hide Password Icon */}
                <button
                  type="button"
                  onClick={handlePasswordToggle}
                  className='flex items-center justify-center w-10 h-full'
                >
                  {showPassword ? <GoEye className='w-6 h-6 text-black opacity-55' /> : <GoEyeClosed className='w-6 h-6 text-black opacity-55' />}
                </button>
              </div>
              {passwordError && <p className='text-red-500 text-sm mt-1'>{passwordError}</p>}
            </div>

            <div className='mt-5'>
              <label className='mb-2 flex gap-1 items-start text-sm ml-2 font-medium'>{t('Company name')} <span className='text-[#ef4444]'>*</span></label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className='rounded-[10px] min-h-[50px] px-5 mb-1 border border-[#E5E7EB] placeholder:font-light w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'
              />
              {companyError && <p className='text-red-500 text-sm mt-1'>{companyError}</p>}
            </div>

            <button
              type="submit"
              className='h-[52px] px-5 text-center bg-[#0b1223] text-white text-base capitalize rounded-lg mt-6 mb-4 w-full font-medium transition-colors duration-300'
            >
              {t('signup')}
            </button>

            <div className='mb-3 text-xs text-[#918d8d] flex items-center justify-center text-center font-bold'>
              {t('signuptext1')}&nbsp;
              <a
                href="/terms-of-use"
                className='text-[#1976d2]'
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('termsofuse')}
              </a>.
            </div>

            <div className='text-sm text-[#4b5563] flex items-center gap-1 justify-center text-center font-bold'>
              {t('alreadyaccount')}
              <button
                type="button"
                className='text-[#1976d2] cursor-pointer bg-transparent border-none p-0 font-inherit text-inherit underline'
                onClick={onAlreadyAccount}
              >
                {t('login2')}
              </button>
            </div>
          </form>
        </div>

        {/* Overlay */}
        <div
          className='fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-[60]'
          onClick={onClose}
        />
        <Toast message={toastMessage} type={toastType} />
      </div>
    </>,
    document.body,
  );
};

export default SignupModal;
